import { Box, Center, Flex } from "@mantine/core";
import { Dropzone } from '@mantine/dropzone';
import { IconFileFilled, IconPhoto, IconUpload, IconX } from "@tabler/icons-react";
import { Button, Modal, Text } from "components";
import { IRootState } from "config/redux";
import { ServiceConstants } from "constant";
import { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { CreditService } from "services";

const { REQUEST_CREDIT_ATTACHMENT_UPLOAD_ID, REQUEST_PENDING } = ServiceConstants;

interface AttachmentUploadModalProps {
  creditId: number;
  opened: boolean;
  onClose: () => void;
  t: TFunction;
}

const AttachmentUploadModal = ({ creditId, opened, onClose, t }: AttachmentUploadModalProps) => {
  const uploadServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CREDIT_ATTACHMENT_UPLOAD_ID]);

  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);

  useEffect(() => {
    if (!opened) setSelectedFiles([]);
  }, [opened]);

  const createAttachmentType = useCallback(() => CreditService.creditAttachmentUpload(creditId, selectedFiles), [creditId, selectedFiles]);
  const isLoading = useMemo(() => uploadServiceObject && uploadServiceObject.status === REQUEST_PENDING, [uploadServiceObject]);

  const removeFile = useCallback((file: any) => {
    setSelectedFiles(old => old.filter(el => el !== file))
  }, []);

  const toUpload = useMemo(() => selectedFiles.map((file, index) => (
    <Flex key={`selected-${index}`} align={'center'} mb={4}>
      <Box miw={24} mih={24}>
        <IconFileFilled color={'var(--mantine-color-primary-6)'} width={24} height={24} />
      </Box>
      <Text ml={6}>{file.name}</Text>
      <Button
        variant={'transparent'}
        size={'compact-xs'}
        px={0}
        onClick={() => removeFile(file)}
        miw={24}
        mih={24}
      >
        <IconX color={'var(--mantine-color-error)'} />
      </Button>
    </Flex>
  )), [selectedFiles]);

  return (
    <Modal opened={opened} onClose={onClose} title={t('credit.documentation.delivered.new.title')}>
      <Dropzone
        onDrop={(files) => setSelectedFiles(old => ([...old, ...files]))}
        multiple={true}
        maxSize={30 * 1048576} // 30MB
        accept={['application/pdf, image/png, image/jpeg, image/jpg']}
      >
        <Flex justify={'center'} align={'center'} gap={'sm'} mih={220} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <IconUpload size={52} color="var(--mantine-color-primary-6)" stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={52} color="var(--mantine-color-red-6)" stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconPhoto size={52} color="var(--mantine-color-dimmed)" stroke={1.5} />
          </Dropzone.Idle>
          <Box>
            <Text size={'xl'}>{t('credit.documentation.delivered.new.description.1')}</Text>
            <Text>{t('credit.documentation.delivered.new.description.2')}</Text>
          </Box>
        </Flex>
      </Dropzone>
      <Box mt={toUpload.length > 0 ? 'sm' : 0} >
        {toUpload}
      </Box>
      <Center mt={32}>
        <Button
          onClick={createAttachmentType}
          disabled={isLoading || selectedFiles.length === 0}
          loading={isLoading}
        >
          <Text>{t('action.submit')}</Text>
        </Button>
      </Center>
    </Modal>
  )
}

export default AttachmentUploadModal;